import { z } from "zod";

import {
  DisplayUomOverrideSchema,
  DocumentDisplayUomSettingsSchema,
  emptyDocumentDisplayUomSettings,
} from "~/shared/documents/schemas/document-display-uom.schema";

/** Org key PICK_UP_REQUEST_DOCUMENT_CONFIG — SO Pick Up Request PDF (SC-2494). */
export const PICK_UP_REQUEST_DOCUMENT_CONFIG_VERSION = 1;

export const PickUpRequestDocumentConfigSchema = z
  .object({
    configVersion: z.literal(PICK_UP_REQUEST_DOCUMENT_CONFIG_VERSION).default(PICK_UP_REQUEST_DOCUMENT_CONFIG_VERSION),
    displayLots: z.boolean().default(true),
  })
  .merge(DocumentDisplayUomSettingsSchema);

export type PickUpRequestDocumentConfig = z.infer<typeof PickUpRequestDocumentConfigSchema>;

export const PickUpRequestPdfInputSchema = z.object({
  displayUomOverride: DisplayUomOverrideSchema,
});

export type PickUpRequestPdfInput = z.infer<typeof PickUpRequestPdfInputSchema>;

export const PickUpRequestDocumentConfigForCreateModalSchema = PickUpRequestDocumentConfigSchema.pick({
  displayUom: true,
  allowDisplayUomOverrideOnCreate: true,
});
export type PickUpRequestDocumentConfigForCreateModal = z.infer<typeof PickUpRequestDocumentConfigForCreateModalSchema>;

export const pickPickUpRequestDocumentConfigForCreateModal = (
  config: PickUpRequestDocumentConfig,
): PickUpRequestDocumentConfigForCreateModal => PickUpRequestDocumentConfigForCreateModalSchema.parse(config);

export const emptyPickUpRequestDocumentConfig = (): PickUpRequestDocumentConfig => ({
  configVersion: PICK_UP_REQUEST_DOCUMENT_CONFIG_VERSION,
  displayLots: true,
  ...emptyDocumentDisplayUomSettings(),
});

export const tryParsePickUpRequestDocumentConfigString = (raw: string): PickUpRequestDocumentConfig | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = PickUpRequestDocumentConfigSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

export const parsePickUpRequestDocumentConfigForSettings = (raw: string | null | undefined): PickUpRequestDocumentConfig => {
  if (!raw?.trim()) {
    return emptyPickUpRequestDocumentConfig();
  }
  const parsed = tryParsePickUpRequestDocumentConfigString(raw);
  if (!parsed) {
    throw new Error("Pick up request document settings could not be read. Contact support before saving.");
  }
  return parsed;
};

export const serializePickUpRequestDocumentConfig = (config: PickUpRequestDocumentConfig): string =>
  JSON.stringify(PickUpRequestDocumentConfigSchema.parse(config));

export const assertPickUpRequestDocumentOrgSettingValueForWrite = (value: string): void => {
  if (!value?.trim()) {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid pick up request document config JSON");
  }
  const result = PickUpRequestDocumentConfigSchema.safeParse(parsed);
  if (!result.success) {
    throw new Error("Invalid pick up request document config schema");
  }
};
